import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, MapPin, Navigation } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MapViewPage = () => {
  const navigate = useNavigate();
  const [assets, setAssets] = useState([]);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAssets();
  }, []);

  const loadAssets = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${API}/assets`, config);
      setAssets(response.data);
    } catch (error) {
      toast.error('Failed to load assets');
    } finally {
      setLoading(false);
    }
  };

  // Calculate bounds for map visualization
  const getMapBounds = () => {
    if (assets.length === 0) return { minLat: -38, maxLat: -37.7, minLon: 144.8, maxLon: 145.3 };
    const lats = assets.map(a => a.latitude);
    const lons = assets.map(a => a.longitude);
    return {
      minLat: Math.min(...lats) - 0.05,
      maxLat: Math.max(...lats) + 0.05,
      minLon: Math.min(...lons) - 0.05,
      maxLon: Math.max(...lons) + 0.05
    };
  };

  const bounds = getMapBounds();
  const mapWidth = 100;
  const mapHeight = 100;

  const getMarkerPosition = (lat, lon) => {
    const x = ((lon - bounds.minLon) / (bounds.maxLon - bounds.minLon)) * mapWidth;
    const y = ((bounds.maxLat - lat) / (bounds.maxLat - bounds.minLat)) * mapHeight;
    return { x, y };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-8" data-testid="map-view-page">
      <div className="max-w-7xl mx-auto">
        <Button
          variant="ghost"
          className="text-gray-600 hover:text-black mb-6"
          onClick={() => navigate('/dashboard')}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>

        <h1 className="text-4xl font-bold text-white mb-8" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Asset Map View
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <Card className="bg-white border-gray-200">
              <CardContent className="p-6">
                <div className="bg-gray-100 rounded-xl p-4 h-[600px] relative overflow-hidden">
                  {/* Map Grid */}
                  <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                    <defs>
                      {/* Victoria coastline approximation */}
                      <linearGradient id="mapGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#1a4d3e" stopOpacity="0.3" />
                        <stop offset="100%" stopColor="#0f2f24" stopOpacity="0.5" />
                      </linearGradient>
                      <pattern id="mapGrid" width="5" height="5" patternUnits="userSpaceOnUse">
                        <path d="M 5 0 L 0 0 0 5" fill="none" stroke="#2a5a4a" strokeWidth="0.1" opacity="0.3"/>
                      </pattern>
                    </defs>
                    
                    {/* Victoria outline approximation */}
                    <rect width="100" height="100" fill="#0f1f1a" />
                    <path
                      d="M 15,30 L 30,25 L 45,22 L 60,20 L 75,22 L 85,28 L 90,40 L 92,55 L 90,70 L 85,80 L 75,88 L 60,92 L 45,90 L 30,85 L 20,75 L 15,60 L 12,45 Z"
                      fill="url(#mapGradient)"
                      stroke="#2a5a4a"
                      strokeWidth="0.3"
                    />
                    <rect width="100" height="100" fill="url(#mapGrid)" />
                    
                    {/* Melbourne metro area highlight */}
                    <ellipse
                      cx="45"
                      cy="55"
                      rx="15"
                      ry="12"
                      fill="#10b981"
                      opacity="0.1"
                      stroke="#10b981"
                      strokeWidth="0.2"
                      strokeDasharray="1,1"
                    />
                    <text x="45" y="67" fontSize="2" fill="#10b981" textAnchor="middle" opacity="0.6">
                      Melbourne
                    </text>
                    
                    {/* Asset markers */}
                    {assets.map((asset) => {
                      const pos = getMarkerPosition(asset.latitude, asset.longitude);
                      const isSelected = selectedAsset?.id === asset.id;
                      const isMobile = asset.asset_type === 'Mobile';
                      return (
                        <g 
                          key={asset.id} 
                          onClick={() => setSelectedAsset(asset)}
                          className="cursor-pointer"
                          data-testid={`map-marker-${asset.id}`}
                        >
                          <circle
                            cx={pos.x}
                            cy={pos.y}
                            r={isSelected ? "2.5" : "1.5"}
                            fill={isMobile ? '#ef4444' : '#10b981'}
                            stroke={isSelected ? '#fff' : 'none'}
                            strokeWidth="0.5"
                            className="transition-all duration-200"
                          />
                          <circle
                            cx={pos.x}
                            cy={pos.y}
                            r={isSelected ? "4" : "3"}
                            fill="none"
                            stroke={isMobile ? '#ef4444' : '#10b981'}
                            strokeWidth="0.3"
                            opacity="0.4"
                          />
                          {isSelected && (
                            <text
                              x={pos.x}
                              y={pos.y - 3}
                              fontSize="2"
                              fill="#fff"
                              textAnchor="middle"
                              className="font-medium"
                            >
                              {asset.name.replace('SodiumQ-', '')}
                            </text>
                          )}
                        </g>
                      );
                    })}
                  </svg>
                  
                  {/* Legend */}
                  <div className="absolute bottom-4 left-4 bg-white border border-gray-200 rounded-lg p-3 space-y-2">
                    <div className="flex items-center space-x-2 text-xs text-black">
                      <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
                      <span>Fixed Assets</span>
                    </div>
                    <div className="flex items-center space-x-2 text-xs text-black">
                      <div className="w-3 h-3 rounded-full bg-red-500"></div>
                      <span>Mobile Assets</span>
                    </div>
                    <div className="text-xs text-gray-400 pt-2 border-t border-white/20">
                      Victoria, Australia
                    </div>
                  </div>

                  {/* Selected Asset Info */}
                  {selectedAsset && (
                    <div className="absolute top-4 right-4 bg-white border border-gray-200 rounded-lg p-4 w-64">
                      <h3 className="text-black font-semibold mb-2">{selectedAsset.name}</h3>
                      <div className="space-y-1 text-xs text-gray-300">
                        <p>Health: <span className="text-emerald-500 font-semibold">{selectedAsset.health_score.toFixed(2)}%</span></p>
                        <p>Safety: <span className="text-emerald-500 font-semibold">{selectedAsset.safety_score.toFixed(2)}%</span></p>
                        <p>SOC: <span className="text-black font-semibold">{selectedAsset.soc.toFixed(2)}%</span></p>
                        <p className="text-gray-600 mt-2">{selectedAsset.location}</p>
                      </div>
                      <Button
                        size="sm"
                        className="w-full mt-3 bg-emerald-500 hover:bg-emerald-600"
                        onClick={() => navigate(`/asset/${selectedAsset.id}`)}
                      >
                        View Details
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          <div>
            <Card className="bg-white border-gray-200">
              <CardContent className="p-6">
                <h3 className="text-black font-semibold mb-4 flex items-center">
                  <Navigation className="w-5 h-5 mr-2 text-emerald-500" />
                  Asset Locations
                </h3>
                <div className="space-y-3 max-h-[560px] overflow-y-auto" data-testid="asset-list">
                  {assets.map((asset) => (
                    <div
                      key={asset.id}
                      onClick={() => {
                        setSelectedAsset(asset);
                        navigate(`/asset/${asset.id}`);
                      }}
                      className="bg-white/5 border border-white/10 rounded-lg p-3 hover:bg-white/10 hover:border-emerald-500/30 cursor-pointer transition-all"
                      data-testid={`map-asset-${asset.id}`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <h4 className="text-black font-medium text-sm">{asset.name}</h4>
                        <Badge variant={asset.asset_type === 'Mobile' ? 'secondary' : 'outline'} className="text-xs">
                          {asset.asset_type}
                        </Badge>
                      </div>
                      <p className="text-xs text-gray-400 flex items-start">
                        <MapPin className="w-3 h-3 mr-1 mt-0.5 flex-shrink-0" />
                        {asset.location}
                      </p>
                      <div className="flex items-center space-x-4 mt-2 text-xs">
                        <span className="text-emerald-500">Health: {asset.health_score.toFixed(2)}%</span>
                        <span className="text-gray-500">SOC: {asset.soc.toFixed(2)}%</span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MapViewPage;
